import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { format } from 'date-fns';

// Extend jsPDF with autotable
declare module 'jspdf' {
  interface jsPDF {
    autoTable: any;
  }
}

interface AttendanceReportData {
  title: string;
  subtitle: string;
  headers: string[];
  rows: any[][];
  summary: {
    label: string;
    value: string | number;
  }[];
  schoolName?: string;
}

export const exportService = {
  generatePDF({ title, subtitle, headers, rows, summary, schoolName = 'Academic Management System' }: AttendanceReportData) {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;

    // 1. Header
    doc.setFontSize(22);
    doc.setTextColor(40, 40, 40);
    doc.text(schoolName, pageWidth / 2, 20, { align: 'center' });

    doc.setFontSize(16);
    doc.setTextColor(100, 100, 100);
    doc.text(title, pageWidth / 2, 30, { align: 'center' });

    doc.setFontSize(10);
    doc.text(subtitle, pageWidth / 2, 38, { align: 'center' });

    // 2. Summary Box
    let currentY = 50;
    doc.setDrawColor(200, 200, 200);
    doc.setFillColor(245, 247, 250);
    doc.rect(14, currentY, pageWidth - 28, 25, 'F');
    
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    
    const summaryWidth = (pageWidth - 28) / summary.length;
    summary.forEach((item, index) => {
      doc.setFont('helvetica', 'bold');
      doc.text(item.label, 20 + (index * summaryWidth), currentY + 8);
      doc.setFont('helvetica', 'normal');
      doc.text(String(item.value), 20 + (index * summaryWidth), currentY + 18);
    });

    currentY += 35;

    // 3. Table
    doc.autoTable({
      head: [headers],
      body: rows,
      startY: currentY,
      theme: 'striped',
      headStyles: { fillColor: [79, 70, 229], textColor: 255 }, // Indigo-600
      alternateRowStyles: { fillColor: [245, 247, 250] },
      margin: { top: 10 },
      styles: { fontSize: 9, cellPadding: 3 },
    });

    // 4. Footer
    const pageCount = (doc as any).internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      const footerText = `Generated on ${format(new Date(), 'PPpp')} | Page ${i} of ${pageCount}`;
      doc.text(footerText, pageWidth / 2, doc.internal.pageSize.height - 10, { align: 'center' });
    }

    // 5. Save
    const fileName = `${title.replace(/\s+/g, '_')}_${format(new Date(), 'yyyy-MM-dd')}.pdf`;
    doc.save(fileName);
  },

  exportToCSV(headers: string[], rows: any[][], fileName: string) {
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${fileName}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};
